// Left rail: field list with filter, selection, editor-only lock, issue hints, context menu.
// Events are delegated on the list root and rows are only rebuilt when something visible changed,
// so a click is never lost to a re-render (e.g. the debounced printer check finishing mid-click).
import { S, subscribe, select, isLocked, setLocked, field } from './state.js';
import { $, h } from './dom.js';
import { icon, FIELD_LETTER, FIELD_ICON_CLASS, KIND_LABEL } from './icons.js';
import { displayText } from './geometry.js';
import { issuesFor } from './check.js';
import { openFieldContextMenu } from './menus.js';
import { scrollToField } from './view.js';

let lastSig = null;

function subtitle(f) {
  const pos = `@ ${(f.x / 100).toFixed(1)}, ${(f.y / 100).toFixed(1)} mm`;
  if (f.kind === 'unsupported') return 'Unsupported — kept unchanged';
  if (f.kind === 'date') return `${pos} · ${displayText(f)}`;
  if (f.kind === 'barcode') return `${pos} · ${f.symbology || 'barcode'}`;
  return `${pos} · ${KIND_LABEL[f.kind].toLowerCase()}`;
}

function model(f) {
  const issues = issuesFor(f.key);
  const errors = issues.filter((i) => i.severity === 'error').length;
  return {
    key: f.key,
    kind: f.kind,
    name: f.name,
    sub: subtitle(f),
    selected: S.selection.includes(f.key),
    locked: isLocked(f),
    errors,
    warnings: issues.length - errors,
    tip: issues.map((i) => `${i.severity === 'error' ? 'Error' : 'Warning'}${i.code ? ` ${i.code}` : ''}: ${i.message}`).join('\n'),
  };
}

function row(m) {
  const lockBtn =
    m.kind === 'unsupported'
      ? h('span', { class: 'field-act on', title: 'Unsupported field — kept unchanged', 'aria-hidden': 'true' }, icon('lock', ''))
      : h(
          'button',
          {
            class: `field-act${m.locked ? ' on' : ''}`,
            type: 'button',
            dataset: { act: 'lock' },
            title: `${m.locked ? 'Unlock' : 'Lock'} — editor only, not saved to the file`,
            'aria-label': `${m.locked ? 'Unlock' : 'Lock'} ${m.name} (editor only)`,
            'aria-pressed': String(m.locked),
          },
          icon(m.locked ? 'lock' : 'unlock', ''),
        );
  return h(
    'div',
    {
      class: `field-row${m.selected ? ' selected' : ''}${m.locked ? ' locked' : ''}`,
      role: 'option',
      tabindex: '0',
      'aria-selected': String(m.selected),
      'aria-label': `${m.name}, ${KIND_LABEL[m.kind] || m.kind}${m.locked ? ', locked' : ''}${m.errors ? `, ${m.errors} error${m.errors === 1 ? '' : 's'}` : ''}`,
      dataset: { key: m.key },
    },
    h('div', { class: `field-icon ${FIELD_ICON_CLASS[m.kind] || 'unknown'}`, 'aria-hidden': 'true' }, FIELD_LETTER[m.kind] || '·'),
    h(
      'div',
      { class: 'field-meta' },
      h(
        'div',
        { class: 'field-name' },
        m.name,
        m.errors || m.warnings ? h('span', { style: { marginLeft: '6px', color: m.errors ? 'var(--bad)' : 'var(--warn)', fontSize: '10px' }, title: m.tip }, m.errors ? '●' : '▲') : null,
      ),
      h('div', { class: 'field-coord' }, m.sub),
    ),
    h('div', { class: 'field-actions', style: m.locked && !m.selected ? { display: 'flex' } : null }, lockBtn),
  );
}

function render() {
  const root = $('#field-list-body');
  $('#field-count').textContent = String(S.fields.length);
  if (!S.label || !S.fields.length) {
    const sig = S.label ? 'empty' : 'none';
    if (sig === lastSig) return;
    lastSig = sig;
    root.replaceChildren(
      S.label
        ? h('div', { class: 'empty' }, h('span', { class: 'empty-strong' }, 'Empty label'), 'Add a text, date or barcode field from the toolbar.')
        : h('div', { class: 'empty' }, h('span', { class: 'empty-strong' }, 'No file open'), 'Drop a .itf file or use Open.'),
    );
    return;
  }
  const q = S.filter.trim().toLowerCase();
  const models = S.fields
    .filter((f) => !q || `${f.name} ${f.kind === 'text' ? f.text : ''} ${displayText(f)}`.toLowerCase().includes(q))
    .map(model);
  const sig = JSON.stringify(models);
  if (sig === lastSig) return;
  lastSig = sig;

  const focusedKey = document.activeElement && root.contains(document.activeElement) ? document.activeElement.closest('.field-row')?.dataset.key : null;
  const wrap = h('div', { class: 'field-list', role: 'listbox', 'aria-label': 'Fields', 'aria-multiselectable': 'true' }, models.map(row));
  if (!models.length) wrap.appendChild(h('div', { class: 'empty' }, 'No fields match the filter.'));
  root.replaceChildren(wrap);
  if (focusedKey) {
    const again = root.querySelector(`[data-key="${CSS.escape(focusedKey)}"]`);
    if (again) again.focus({ preventScroll: true });
  }
  if (S.selection.length === 1) {
    const sel = wrap.querySelector('.field-row.selected');
    if (sel) {
      const a = sel.getBoundingClientRect();
      const b = root.getBoundingClientRect();
      if (a.top < b.top || a.bottom > b.bottom) sel.scrollIntoView({ block: 'nearest' });
    }
  }
}

function pick(rowEl, e) {
  const key = rowEl.dataset.key;
  const multi = e.shiftKey || e.metaKey || e.ctrlKey;
  select([key], { toggle: multi });
  if (!multi) scrollToField(key);
}

export function initFieldList() {
  const root = $('#field-list-body');
  $('#field-search-input').addEventListener('input', (e) => {
    S.filter = e.target.value;
    render();
  });
  root.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || e.target.closest('[data-act]')) return;
    const rowEl = e.target.closest('.field-row');
    if (rowEl) pick(rowEl, e);
  });
  root.addEventListener('click', (e) => {
    const act = e.target.closest('[data-act="lock"]');
    if (!act) return;
    e.stopPropagation();
    const f = field(act.closest('.field-row').dataset.key);
    if (f) setLocked(f, !isLocked(f));
  });
  root.addEventListener('keydown', (e) => {
    const rowEl = e.target.closest && e.target.closest('.field-row');
    if (!rowEl || e.target.closest('[data-act]')) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      pick(rowEl, e);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      const next = e.key === 'ArrowDown' ? rowEl.nextElementSibling : rowEl.previousElementSibling;
      if (next && next.classList.contains('field-row')) next.focus();
    } else if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
      e.preventDefault();
      const r = rowEl.getBoundingClientRect();
      openFieldContextMenu(r.left + 24, r.bottom, rowEl.dataset.key);
    }
  });
  root.addEventListener('contextmenu', (e) => {
    const rowEl = e.target.closest('.field-row');
    if (!rowEl) return;
    e.preventDefault();
    openFieldContextMenu(e.clientX, e.clientY, rowEl.dataset.key);
  });
  subscribe((r) => {
    if (r.has('doc') || r.has('selection') || r.has('issues')) render();
  });
  render();
}
